import { useState } from 'react';
import { useSelector } from 'react-redux';
import XLSX from 'xlsx';
import { round } from 'lodash';
import { ButtonV2 as Button } from '@praktio/common-ui';

import { RootState } from '../../../../ducks/store';
import { Timezone } from '../../../../utils/constants';
import { formateDateTime } from '../LoadDateTime/LoadDateTime';
import { generateReportXlsxFileName } from '../../../../utils/functions';
import { CourseName, Learner } from '../TableSummaryReport/TableSummaryReport.types';

type Props = {
  learnerData: Optional<Learner[]>;
  courseData: Optional<CourseName[]>;
};

const DownloadSummaryReportButton = ({ learnerData, courseData }: Props) => {
  const missingData = !learnerData || !courseData;

  const [downloadDisabled, setDownloadDisabled] = useState(missingData);

  const { user, loadDate } = useSelector((state: RootState) => ({
    user: state.auth.authenticatedUser,
    loadDate: new Date(state.report.loadDate),
  }));

  const handleDownload = () => {
    if (downloadDisabled || !learnerData || !courseData) return;

    setDownloadDisabled(true);

    setTimeout(() => {
      setDownloadDisabled(false);
    }, 2000);

    const loadTime = formateDateTime(loadDate);

    const workbook = XLSX.utils.book_new();

    const courseNames = courseData?.map((course: CourseName) => course.title) || [];
    const headers = ['Name', 'Email', 'Total % Complete', ...courseNames];

    const topRow = [
      `This report was downloaded on ${loadTime}, by ${user?.firstName} ${user?.lastName}. All dates and times are in their local time zone (${Timezone}).`,
    ];

    // Create learner data rows
    const data = learnerData?.map((learner) => {
      const courseProgress = learner.courseProgress || {};

      const progress =
        courseData?.map((course: CourseName) => {
          return courseProgress[course._id] || courseProgress[course._id] === 0
            ? courseProgress[course._id]
            : 'N/A';
        }) || [];

      return [learner.name, learner.email, learner.totalPercentageComplete, ...progress];
    });

    // Create average row
    const averageRow = [
      'Average',
      '',
      round(
        learnerData?.reduce((acc, learner) => acc + (learner?.totalPercentageComplete || 0), 0) /
          learnerData?.length,
        2,
      ),
      ...(courseData?.map(({ _id }) => {
        const total = learnerData?.reduce((acc, learner) => {
          const courseProgress = learner.courseProgress[_id];
          return courseProgress || courseProgress === 0 ? acc + courseProgress : acc;
        }, 0);

        const rowsLength = learnerData?.filter((learner) => {
          const courseProgress = learner.courseProgress[_id];
          return courseProgress || courseProgress === 0;
        }).length;

        if (!total || !rowsLength) return 0;

        return round(total / rowsLength, 2);
      }) || []),
    ];

    const worksheet = XLSX.utils.aoa_to_sheet([topRow, headers, ...data, averageRow]);

    const range = XLSX.utils.decode_range(worksheet['!ref'] as string);

    // Convert values to percentages (excluding the first two columns: Name and Email)
    for (let row = 2; row < range.e.r + 1; row++) {
      for (let cell = 2; cell < range.e.c + 1; cell++) {
        const tableCell = worksheet[XLSX.utils.encode_cell({ r: row, c: cell })];

        if (!tableCell || tableCell.v === 'N/A') continue;

        tableCell.v = tableCell.v / 100;
        tableCell.t = 'n';
        tableCell.z = '0.00%';
      }
    }

    XLSX.utils.book_append_sheet(workbook, worksheet, 'Summary Report');
    XLSX.writeFile(workbook, generateReportXlsxFileName('Praktio Summary Report', loadDate));
  };

  return (
    <Button
      disabled={downloadDisabled}
      variant="primary"
      icon={{ name: 'download', position: 'left' }}
      onClick={handleDownload}
    >
      {missingData ? 'Oh no! Something went wrong' : 'Download Report'}
    </Button>
  );
};

export default DownloadSummaryReportButton;
